import { useState } from "react";
import { Link } from "react-router-dom";
import { CalendarCheck, CalendarPlus, FileText, Plus, UserPlus, Wrench } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { vociDi } from "@/lib/accessi";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";

/* «Nuovo»: i gesti di ogni giorno a un tocco da qualsiasi schermata. Porta SOLO dove il gestionale va già:
   ogni voce apre un flusso che esiste (il dialogo «Nuovo cliente», «Nuovo appuntamento» del Calendario senza
   cliente scelto, le visite senza esito, il calcolatore, «Nuova assistenza»).
   Chi vede una voce: chi ha la sezione dove porta, come nel menu. Il montatore non crea assistenze né
   appuntamenti (non vede i clienti da scegliere). */
const VOCI = [
  { id: "cliente", voce: "clienti", label: "Cliente", breve: "cliente", hint: "Da WhatsApp, Instagram, una telefonata", to: "/clienti?nuovo=1", icon: UserPlus },
  { id: "appuntamento", voce: "calendario", label: "Appuntamento", breve: "visita", hint: "Visita, rilievo, consegna", to: "/calendario?nuovo=1", icon: CalendarPlus, tranne: ["montatore"] },
  { id: "esito", voce: "clienti", label: "Com'è andata una visita", breve: "visita", hint: "Le visite in showroom senza esito", to: "/clienti?lista=da_confermare", icon: CalendarCheck },
  { id: "preventivo", voce: "calcolatore", label: "Preventivo", breve: "preventivo", hint: "Parte dal calcolatore", to: "/preventivi/calcolatore", icon: FileText },
  { id: "assistenza", voce: "assistenza", label: "Assistenza", breve: "assistenza", hint: "Per un cliente già servito", to: "/assistenza?nuova=1", icon: Wrench, tranne: ["montatore"] },
];

export function vociNuovo(ruolo, spente) {
  const mie = vociDi(ruolo, spente);
  return VOCI.filter((v) => mie.includes(v.voce) && !(v.tranne || []).includes(ruolo));
}

function Icona({ icon: Icon }) {
  return (
    <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-subtle text-brand-ink" aria-hidden="true">
      <Icon className="size-5" strokeWidth={1.75} />
    </span>
  );
}

function Testo({ v }) {
  return (
    <span className="min-w-0">
      <span className="block font-semibold text-ink-strong">{v.label}</span>
      <span className="block text-small text-ink-2">{v.hint}</span>
    </span>
  );
}

/** Sul computer e in testata: un menu sotto il pulsante. `suMenu` = il pulsante largo del menu blu. */
export function NuovoMenu({ suMenu = false }) {
  const { profile } = useAuth() || {};
  const voci = vociNuovo(profile?.ruolo, profile?.sezioni_spente);
  if (!voci.length) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid="nuovo-trigger"
          className={cn(
            "flex h-11 items-center gap-2.5 rounded-md bg-nav-button px-3 font-bold text-nav-button-ink transition-colors duration-150 ease-enter hover:bg-nav-button-hover",
            suMenu ? "w-full text-left" : "shrink-0",
          )}
        >
          <Plus className="size-5" strokeWidth={2} aria-hidden="true" />
          <span className={suMenu ? "flex-1" : undefined}>Nuovo</span>
          {suMenu ? (
            <span className="truncate text-caption font-medium text-ink-3">
              {voci.slice(0, 2).map((v) => v.breve).join(", ")}…
            </span>
          ) : null}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" sideOffset={6} className="ida-motion w-80 p-1.5 shadow-overlay">
        {voci.map((v) => (
          <DropdownMenuItem key={v.id} asChild className="cursor-pointer gap-3 rounded-sm px-2 py-2">
            <Link to={v.to} data-testid={`nuovo-voce-${v.id}`}>
              <Icona icon={v.icon} />
              <Testo v={v} />
            </Link>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Sul telefono: il pulsante blu della barra in basso apre un foglio dal basso. */
export function NuovoFoglio() {
  const { profile } = useAuth() || {};
  const [aperto, setAperto] = useState(false);
  const voci = vociNuovo(profile?.ruolo, profile?.sezioni_spente);
  if (!voci.length) return null;
  return (
    <>
      <button
        type="button"
        data-testid="nuovo-trigger"
        aria-haspopup="dialog"
        aria-label={`Nuovo: ${voci.map((v) => v.breve).join(", ")}`}
        onClick={() => setAperto(true)}
        className="inline-flex h-12 items-center justify-center gap-1.5 rounded-lg bg-brand font-bold text-on-brand transition-colors duration-150 ease-enter hover:bg-brand-hover"
      >
        <Plus className="size-5" strokeWidth={2.2} aria-hidden="true" />
        Nuovo
      </button>
      <Drawer open={aperto} onOpenChange={setAperto} shouldScaleBackground={false}>
        <DrawerContent data-testid="nuovo-foglio" className="px-4 pb-6">
          <DrawerTitle className="mb-1.5 mt-3">Nuovo</DrawerTitle>
          <DrawerDescription className="sr-only">Scegli cosa aggiungere: si apre la pagina giusta.</DrawerDescription>
          <ul>
            {voci.map((v) => (
              <li key={v.id}>
                <Link
                  to={v.to}
                  onClick={() => setAperto(false)}
                  data-testid={`nuovo-voce-${v.id}`}
                  className="flex min-h-14 items-center gap-3.5 border-b border-line-subtle py-2 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand"
                >
                  <Icona icon={v.icon} />
                  <Testo v={v} />
                </Link>
              </li>
            ))}
          </ul>
        </DrawerContent>
      </Drawer>
    </>
  );
}
